import { useEffect, useSyncExternalStore } from 'react';
import { supabaseClient } from '../config/supabase';

export const MENU_OPEN_HOVER = 'hover';
export const MENU_OPEN_CLICK = 'click';
export const DEFAULT_MENU_OPEN = MENU_OPEN_HOVER;
export const MENU_OPEN_PREFERENCE_KEY = 'ui.menu_opens_on';

export function normalizeMenuOpenPreference(value) {
    return value === MENU_OPEN_CLICK ? MENU_OPEN_CLICK : DEFAULT_MENU_OPEN;
}

export async function loadMenuOpenPreference(client, userId) {
    if (!client || !userId) return DEFAULT_MENU_OPEN;
    const { data, error } = await client.from('user_preferences')
        .select('value')
        .eq('user_id', userId)
        .eq('key', MENU_OPEN_PREFERENCE_KEY)
        .maybeSingle();
    if (error) throw error;
    return normalizeMenuOpenPreference(data?.value);
}

export async function saveMenuOpenPreference(client, userId, value) {
    if (!client || !userId) throw new Error('Sign in to save your menu preference.');
    const normalized = normalizeMenuOpenPreference(value);
    const { error } = await client.from('user_preferences').upsert({
        user_id: userId,
        key: MENU_OPEN_PREFERENCE_KEY,
        value: normalized,
        updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,key' });
    if (error) throw error;
    return normalized;
}

const stores = new WeakMap();
function preferenceStore(client, userId) {
    if (!stores.has(client)) stores.set(client, new Map());
    const people = stores.get(client);
    const key = userId || '';
    if (!people.has(key)) {
        let state = { value: DEFAULT_MENU_OPEN, loaded: !userId, saving: false, error: '' };
        let started = false;
        const listeners = new Set();
        const publish = patch => {
            state = { ...state, ...patch };
            listeners.forEach(listener => listener());
        };
        people.set(key, {
            subscribe(listener) {
                listeners.add(listener);
                return () => listeners.delete(listener);
            },
            snapshot: () => state,
            load() {
                if (started || !userId) return;
                started = true;
                loadMenuOpenPreference(client, userId)
                    .then(value => publish({ value, loaded: true, error: '' }))
                    .catch(error => {
                        started = false;
                        const message = error?.message || 'Could not load the menu preference.';
                        publish({ loaded: true, error: message });
                        console.error('Error loading menu-open preference:', error);
                    });
            },
            async save(value) {
                if (!userId || state.saving) return;
                const previous = state.value;
                const next = normalizeMenuOpenPreference(value);
                publish({ value: next, saving: true, error: '' });
                try {
                    await saveMenuOpenPreference(client, userId, next);
                    publish({ loaded: true });
                } catch (error) {
                    const message = error?.message || 'Could not save the menu preference.';
                    publish({ value: previous, error: message });
                    throw error;
                } finally {
                    publish({ saving: false });
                }
            },
        });
    }
    return people.get(key);
}

let authUserId = null;
const authListeners = new Set();
let authSubscription;
let authGeneration = 0;
function subscribeAuth(listener) {
    authListeners.add(listener);
    if (authListeners.size === 1) {
        const generation = ++authGeneration;
        const receive = session => {
            if (generation !== authGeneration) return;
            authUserId = session?.user?.id || null;
            authListeners.forEach(callback => callback());
        };
        let authEventReceived = false;
        authSubscription = supabaseClient.auth?.onAuthStateChange?.((_event, session) => {
            authEventReceived = true;
            receive(session);
        })?.data?.subscription;
        supabaseClient.auth?.getSession?.().then(({ data }) => {
            if (!authEventReceived) receive(data?.session);
        }).catch(error => console.error('Error loading the signed-in user for menu preferences:', error));
    }
    return () => {
        authListeners.delete(listener);
        if (!authListeners.size) {
            ++authGeneration;
            authSubscription?.unsubscribe();
            authSubscription = undefined;
            authUserId = null;
        }
    };
}

const authSnapshot = () => authUserId;

export function useMenuOpensPreference(userId, client = supabaseClient) {
    const signedInUserId = useSyncExternalStore(subscribeAuth, authSnapshot, () => null);
    const store = preferenceStore(client, userId || signedInUserId);
    const state = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
    useEffect(() => { store.load(); }, [store]);
    return { ...state, save: store.save };
}

export function useCurrentMenuOpensPreference() {
    const { value } = useMenuOpensPreference(null);
    return value;
}
